import { useState } from "react";
import {
  useTerritorialInspectionDetail,
  useTerritorialInspectionFieldValidationNotes,
  useTerritorialInspectionObjects,
} from "@/hooks";
import { territorialInspectionImageUrl } from "@/lib/api";
import LoadingSkeleton from "@/components/shared/LoadingSkeleton";
import ErrorState from "@/components/shared/ErrorState";
import PreliminaryDataBanner from "./PreliminaryDataBanner";
import {
  ConfidenceBadge,
  FieldValidationStatusBadge,
  PriorityBadge,
  ProcessingStatusBadge,
} from "./TerritorioBadges";

type TabId = "resumo" | "imagem" | "indicadores" | "objetos" | "recomendacoes" | "validacao" | "historico";

const TABS: { id: TabId; label: string; icon: string }[] = [
  { id: "resumo", label: "Resumo", icon: "ri-file-text-line" },
  { id: "imagem", label: "Imagem", icon: "ri-image-line" },
  { id: "indicadores", label: "Indicadores", icon: "ri-bar-chart-2-line" },
  { id: "objetos", label: "Objetos detectados", icon: "ri-focus-3-line" },
  { id: "recomendacoes", label: "Recomendações", icon: "ri-lightbulb-line" },
  { id: "validacao", label: "Validação em campo", icon: "ri-shield-check-line" },
  { id: "historico", label: "Histórico", icon: "ri-history-line" },
];

interface InspectionDetailDrawerProps {
  inspectionCode: string | null;
  onClose: () => void;
}

function fmt(value: number | null | undefined, suffix = ""): string {
  if (value === null || value === undefined) return "—";
  return `${value}${suffix}`;
}

export default function InspectionDetailDrawer({ inspectionCode, onClose }: InspectionDetailDrawerProps) {
  const [activeTab, setActiveTab] = useState<TabId>("resumo");
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const { data: detail, loading, error } = useTerritorialInspectionDetail(inspectionCode);
  const { objects, loading: objectsLoading } = useTerritorialInspectionObjects(inspectionCode);
  const { notes, loading: notesLoading } = useTerritorialInspectionFieldValidationNotes(inspectionCode);

  if (!inspectionCode) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-2xl bg-white z-50 shadow-xl overflow-y-auto">
        <div className="p-6 space-y-4">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="text-xs font-mono text-foreground-500">{inspectionCode}</div>
              <h2 className="text-lg font-bold text-foreground-900 font-heading mt-0.5">
                {detail ? `${detail.neighborhood || "Sem bairro"} · Quadra ${detail.blockCode || "—"}` : "Carregando..."}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-md hover:bg-background-100 flex items-center justify-center transition-colors shrink-0"
              aria-label="Fechar"
            >
              <i className="ri-close-line text-lg text-foreground-500" />
            </button>
          </div>

          <PreliminaryDataBanner />

          {loading && <LoadingSkeleton type="card" count={2} />}
          {error && <ErrorState message="Não foi possível carregar o detalhe da inspeção." />}

          {detail && (
            <>
              <div className="flex flex-wrap gap-2">
                <ProcessingStatusBadge status={detail.processingStatus} />
                <FieldValidationStatusBadge status={detail.fieldValidationStatus} />
                <ConfidenceBadge score={detail.confidenceScore} />
              </div>

              <div className="flex flex-wrap gap-1 border-b border-background-200/70">
                {TABS.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-3 py-2 text-xs font-medium rounded-t-md flex items-center gap-1.5 transition-colors cursor-pointer ${
                      activeTab === tab.id
                        ? "bg-primary-50 text-primary-700 border-b-2 border-primary-500"
                        : "text-foreground-500 hover:text-foreground-700"
                    }`}
                  >
                    <i className={`${tab.icon} text-sm`} />
                    {tab.label}
                  </button>
                ))}
              </div>

              {activeTab === "resumo" && (
                <div className="space-y-4">
                  <div className="bg-background-50 rounded-lg p-4">
                    <div className="text-xs font-semibold text-foreground-700 mb-2">
                      {detail.reportTitle || "Relatório técnico"}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed whitespace-pre-line">
                      {detail.reportText || "Sem relatório técnico registrado para esta inspeção."}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-background-50 rounded-lg p-3">
                      <div className="text-[11px] text-foreground-400 mb-0.5">Árvores estimadas</div>
                      <div className="text-sm font-medium text-foreground-800">{fmt(detail.estimatedTreeCount)}</div>
                    </div>
                    <div className="bg-background-50 rounded-lg p-3">
                      <div className="text-[11px] text-foreground-400 mb-0.5">Cobertura de copa</div>
                      <div className="text-sm font-medium text-foreground-800">{fmt(detail.canopyCoveragePercent, "%")}</div>
                    </div>
                    <div className="bg-background-50 rounded-lg p-3">
                      <div className="text-[11px] text-foreground-400 mb-0.5">Potencial de plantio</div>
                      <div className="text-sm font-medium text-foreground-800">{fmt(detail.plantingPotentialCount)}</div>
                    </div>
                    <div className="bg-background-50 rounded-lg p-3">
                      <div className="text-[11px] text-foreground-400 mb-0.5">Déficit arbóreo</div>
                      <div className="text-sm font-medium text-foreground-800">{fmt(detail.treeDeficitCount)}</div>
                    </div>
                    <div className="bg-background-50 rounded-lg p-3">
                      <div className="text-[11px] text-foreground-400 mb-0.5">Árvores em risco</div>
                      <div className="text-sm font-medium text-foreground-800">{fmt(detail.riskTreeCount)}</div>
                    </div>
                    <div className="bg-background-50 rounded-lg p-3">
                      <div className="text-[11px] text-foreground-400 mb-0.5">Responsável</div>
                      <div className="text-sm font-medium text-foreground-800">{detail.responsiblePartyName || "—"}</div>
                    </div>
                  </div>

                  {detail.roads.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-foreground-700 mb-2">Sistema viário</div>
                      <div className="space-y-1.5">
                        {detail.roads.map((road, i) => (
                          <div key={i} className="text-xs text-foreground-600 bg-background-50 rounded-md px-3 py-2">
                            <span className="font-medium">{road.roadName || "Via sem nome"}</span>
                            {road.pavementType && ` · pavimento ${road.pavementType}`}
                            {road.sidewalkCondition && ` · calçada ${road.sidewalkCondition}`}
                            {road.plantingPotentialCount != null && ` · potencial de plantio: ${road.plantingPotentialCount}`}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {detail.coverage.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-foreground-700 mb-2">Cobertura territorial</div>
                      <div className="grid grid-cols-2 gap-1.5">
                        {detail.coverage.map((cov, i) => (
                          <div key={i} className="text-xs text-foreground-600 bg-background-50 rounded-md px-3 py-2">
                            <span className="font-medium">{cov.coverageType}</span>
                            {cov.areaPercent != null && ` — ${cov.areaPercent}%`}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === "imagem" && (
                <div className="space-y-2">
                  {!detail.imageFilename && (
                    <p className="text-xs text-foreground-500">
                      Nenhuma imagem de origem registrada para esta inspeção.
                    </p>
                  )}
                  {detail.imageFilename && imageLoadFailed && (
                    <p className="text-xs text-foreground-500">
                      Não foi possível carregar a imagem de origem ({detail.imageFilename}).
                    </p>
                  )}
                  {detail.imageFilename && !imageLoadFailed && (
                    <div className="border border-background-200/70 rounded-lg overflow-hidden bg-background-50">
                      <img
                        src={territorialInspectionImageUrl(detail.inspectionCode)}
                        alt={`Ortoimagem de origem — ${detail.imageFilename}`}
                        className="w-full h-auto"
                        onError={() => setImageLoadFailed(true)}
                      />
                    </div>
                  )}
                  {detail.imageFilename && (
                    <p className="text-[11px] font-mono text-foreground-400">{detail.imageFilename}</p>
                  )}
                </div>
              )}

              {activeTab === "indicadores" && (
                <div className="space-y-1.5">
                  {detail.indicators.length === 0 && (
                    <p className="text-xs text-foreground-500">Nenhum indicador registrado para esta inspeção.</p>
                  )}
                  {detail.indicators.map((ind, i) => (
                    <div key={i} className="flex items-center justify-between bg-background-50 rounded-md px-3 py-2">
                      <div>
                        <div className="text-xs font-medium text-foreground-800">{ind.indicatorName || ind.indicatorCode}</div>
                        {ind.notes && <div className="text-[11px] text-foreground-500 mt-0.5">{ind.notes}</div>}
                      </div>
                      <div className="text-right shrink-0 ml-3">
                        <div className="text-sm font-semibold text-foreground-800">
                          {ind.valueNumeric != null ? `${ind.valueNumeric}${ind.unit ? ` ${ind.unit}` : ""}` : ind.valueText || "—"}
                        </div>
                        <ConfidenceBadge score={ind.confidenceScore} />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === "objetos" && (
                <div className="space-y-1.5">
                  {objectsLoading && <LoadingSkeleton type="table" count={3} />}
                  {!objectsLoading && objects.length === 0 && (
                    <p className="text-xs text-foreground-500">Nenhum objeto detectado registrado para esta inspeção.</p>
                  )}
                  {objects.map((obj) => (
                    <div key={obj.uuid} className="flex items-center justify-between bg-background-50 rounded-md px-3 py-2">
                      <div>
                        <div className="text-xs font-medium text-foreground-800">
                          {obj.objectLabel || obj.objectCode || obj.objectType}
                        </div>
                        <div className="text-[11px] text-foreground-500 mt-0.5">{obj.objectType}</div>
                      </div>
                      <ConfidenceBadge score={obj.confidenceScore} />
                    </div>
                  ))}
                </div>
              )}

              {activeTab === "recomendacoes" && (
                <div className="space-y-1.5">
                  {detail.recommendations.length === 0 && (
                    <p className="text-xs text-foreground-500">Nenhuma recomendação registrada para esta inspeção.</p>
                  )}
                  {detail.recommendations.map((rec, i) => (
                    <div key={i} className="bg-background-50 rounded-md px-3 py-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-foreground-800">{rec.recommendationType}</span>
                        <PriorityBadge priority={rec.priority} />
                      </div>
                      <p className="text-xs text-foreground-600">{rec.description}</p>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === "validacao" && (
                <div className="space-y-1.5">
                  {notesLoading && <LoadingSkeleton type="table" count={3} />}
                  {!notesLoading && notes.length === 0 && (
                    <p className="text-xs text-foreground-500">
                      Nenhuma observação de validação em campo registrada para esta inspeção.
                    </p>
                  )}
                  {notes.map((note) => (
                    <div key={note.uuid} className="bg-background-50 rounded-md px-3 py-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-foreground-800">{note.noteType}</span>
                        <PriorityBadge priority={note.priority} />
                      </div>
                      <p className="text-xs text-foreground-600">{note.description}</p>
                      {note.responsiblePartyName && (
                        <p className="text-[11px] text-foreground-400 mt-1">Responsável: {note.responsiblePartyName}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {activeTab === "historico" && (
                <div className="space-y-2">
                  <div className="bg-background-50 rounded-md px-3 py-2 text-xs text-foreground-600">
                    <span className="font-medium">Inspeção anterior: </span>
                    {detail.previousInspectionCode || "nenhuma — esta é a primeira inspeção registrada para esta quadra."}
                  </div>
                  <div className="bg-background-50 rounded-md px-3 py-2 text-xs text-foreground-600">
                    <span className="font-medium">Criada em: </span>
                    {new Date(detail.createdAt).toLocaleString("pt-BR")}
                  </div>
                  <div className="bg-background-50 rounded-md px-3 py-2 text-xs text-foreground-600">
                    <span className="font-medium">Última atualização: </span>
                    {new Date(detail.updatedAt).toLocaleString("pt-BR")}
                  </div>
                  <p className="text-[11px] text-foreground-400">
                    Comparação temporal completa de indicadores por bairro/quadra estará disponível quando houver mais
                    de uma inspeção para a mesma quadra.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
